var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, ButtonBase, IconButton, InputAdornment, MenuItem, Popover, Select, TextField, Typography, } from "@mui/material";
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, } from "date-fns";
var ACCENT = "rgb(255, 184, 1)";
var ACCENT_BG = "rgba(255, 184, 1, 0.08)";
var ACCENT_BORDER = "rgba(255, 184, 1, 0.2)";
var BG_CARD = "#13131E";
var BORDER = "#1F2937";
var WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
var HOURS = Array.from({ length: 24 }, function (_, index) { return String(index).padStart(2, "0"); });
var MINUTES = Array.from({ length: 12 }, function (_, index) { return String(index * 5).padStart(2, "0"); });
var menuPaperSx = {
    background: "#1a2235",
    border: "1px solid ".concat(BORDER),
    color: "#fff",
    "& .MuiMenuItem-root": {
        color: "#fff",
        fontSize: 13,
        "&:hover": { background: ACCENT_BG },
        "&.Mui-selected": { background: ACCENT_BG, color: ACCENT },
    },
};
var readDateParts = function (value) {
    var match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
    if (!match)
        return null;
    var year = match[1], month = match[2], day = match[3], _a = match[4], hour = _a === void 0 ? "12" : _a, _b = match[5], minute = _b === void 0 ? "00" : _b;
    var date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
    return Number.isNaN(date.getTime()) ? null : date;
};
var formatValue = function (date, mode) {
    return mode === "date" ? format(date, "yyyy-MM-dd") : format(date, "yyyy-MM-dd'T'HH:mm");
};
var formatDisplayValue = function (date, mode) {
    if (!date)
        return "";
    return mode === "date" ? format(date, "MMM d, yyyy") : format(date, "MMM d, yyyy, HH:mm");
};
var createDateWithTime = function (day, source) {
    var _a, _b;
    var next = new Date(day);
    next.setHours((_a = source === null || source === void 0 ? void 0 : source.getHours()) !== null && _a !== void 0 ? _a : 12, (_b = source === null || source === void 0 ? void 0 : source.getMinutes()) !== null && _b !== void 0 ? _b : 0, 0, 0);
    return next;
};
export var AdminDateTimeField = function (_a) {
    var label = _a.label, value = _a.value, onChange = _a.onChange, _b = _a.mode, mode = _b === void 0 ? "datetime" : _b, fullWidth = _a.fullWidth, required = _a.required, disabled = _a.disabled, error = _a.error, helperText = _a.helperText, sx = _a.sx;
    var selectedDate = useMemo(function () { return readDateParts(value); }, [value]);
    var _c = useState(null), anchorEl = _c[0], setAnchorEl = _c[1];
    var _d = useState(selectedDate !== null && selectedDate !== void 0 ? selectedDate : new Date()), viewMonth = _d[0], setViewMonth = _d[1];
    var isOpen = Boolean(anchorEl);
    var days = useMemo(function () {
        var start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
        var end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
        return eachDayOfInterval({ start: start, end: end });
    }, [viewMonth]);
    var openPicker = function (target) {
        if (disabled)
            return;
        setViewMonth(selectedDate !== null && selectedDate !== void 0 ? selectedDate : new Date());
        setAnchorEl(target);
    };
    var commitDate = function (date) {
        onChange(formatValue(date, mode));
    };
    var selectDay = function (day) {
        var next = createDateWithTime(day, selectedDate);
        commitDate(next);
        if (mode === "date")
            setAnchorEl(null);
    };
    var setNow = function () {
        var now = new Date();
        commitDate(now);
        setViewMonth(now);
    };
    var setTimePart = function (part, nextValue) {
        var next = selectedDate ? new Date(selectedDate) : new Date();
        if (part === "hour")
            next.setHours(Number(nextValue));
        if (part === "minute")
            next.setMinutes(Number(nextValue));
        next.setSeconds(0, 0);
        commitDate(next);
        setViewMonth(next);
    };
    return (_jsxs(_Fragment, { children: [_jsx(TextField, { label: label, value: formatDisplayValue(selectedDate, mode), onClick: function (event) { return openPicker(event.currentTarget); }, onKeyDown: function (event) {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openPicker(event.currentTarget);
                    }
                }, fullWidth: fullWidth, required: required, disabled: disabled, error: error, helperText: helperText, placeholder: mode === "date" ? "Select date" : "Select date and time", slotProps: {
                    input: {
                        readOnly: true,
                        endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CalendarTodayIcon, { sx: { color: isOpen ? ACCENT : "rgba(255,255,255,0.45)", fontSize: 18 } }) })),
                    },
                    inputLabel: { shrink: Boolean(selectedDate) || isOpen },
                }, sx: __assign({ cursor: disabled ? "default" : "pointer", "& .MuiInputBase-input": { cursor: disabled ? "default" : "pointer" } }, sx) }), _jsx(Popover, { open: isOpen, anchorEl: anchorEl, onClose: function () { return setAnchorEl(null); }, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" }, PaperProps: {
                    sx: {
                        mt: 1,
                        width: 328,
                        background: BG_CARD,
                        border: "1px solid ".concat(BORDER),
                        borderRadius: "14px",
                        color: "#fff",
                        boxShadow: "0 18px 50px rgba(0,0,0,0.38)",
                        overflow: "hidden",
                    },
                }, children: _jsxs(Box, { sx: { p: 1.5 }, children: [_jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, children: [_jsx(IconButton, { size: "small", onClick: function () { return setViewMonth(function (current) { return addMonths(current, -1); }); }, sx: { color: "rgba(255,255,255,0.65)", "&:hover": { color: ACCENT, background: ACCENT_BG } }, children: _jsx(ChevronLeftIcon, { fontSize: "small" }) }), _jsx(Typography, { sx: { color: "#fff", fontWeight: 700, fontSize: 14 }, children: format(viewMonth, "MMMM yyyy") }), _jsx(IconButton, { size: "small", onClick: function () { return setViewMonth(function (current) { return addMonths(current, 1); }); }, sx: { color: "rgba(255,255,255,0.65)", "&:hover": { color: ACCENT, background: ACCENT_BG } }, children: _jsx(ChevronRightIcon, { fontSize: "small" }) })] }), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }, children: WEEK_DAYS.map(function (day) { return (_jsx(Typography, { sx: { color: "rgba(255,255,255,0.35)", fontSize: 11, textAlign: "center", fontWeight: 700 }, children: day }, day)); }) }), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }, children: days.map(function (day) {
                                var selected = Boolean(selectedDate && isSameDay(day, selectedDate));
                                var today = isSameDay(day, new Date());
                                var muted = !isSameMonth(day, viewMonth);
                                return (_jsx(ButtonBase, { type: "button", onClick: function () { return selectDay(day); }, sx: {
                                        width: 38,
                                        height: 34,
                                        border: "1px solid ".concat(selected ? ACCENT_BORDER : "transparent"),
                                        borderRadius: "9px",
                                        background: selected ? ACCENT_BG : today ? "rgba(255,255,255,0.04)" : "transparent",
                                        color: selected ? ACCENT : muted ? "rgba(255,255,255,0.22)" : "#fff",
                                        cursor: "pointer",
                                        fontSize: 13,
                                        fontWeight: selected || today ? 700 : 500,
                                        transition: "background 160ms, color 160ms, border-color 160ms",
                                        "&:hover": {
                                            background: ACCENT_BG,
                                            borderColor: ACCENT_BORDER,
                                            color: ACCENT,
                                        },
                                    }, children: format(day, "d") }, day.toISOString()));
                            }) }), mode === "datetime" && (_jsxs(Box, { sx: {
                                mt: 1.5,
                                pt: 1.5,
                                borderTop: "1px solid ".concat(BORDER),
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }, children: [_jsx(AccessTimeIcon, { sx: { color: "rgba(255,255,255,0.4)", fontSize: 18 } }), _jsx(Select, { size: "small", value: format(selectedDate !== null && selectedDate !== void 0 ? selectedDate : new Date(), "HH"), onChange: function (event) { return setTimePart("hour", event.target.value); }, MenuProps: { PaperProps: { sx: menuPaperSx } }, sx: {
                                        flex: 1,
                                        color: "#fff",
                                        ".MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT_BORDER },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
                                        ".MuiSelect-icon": { color: "rgba(255,255,255,0.45)" },
                                    }, children: HOURS.map(function (hour) { return (_jsx(MenuItem, { value: hour, children: hour }, hour)); }) }), _jsx(Typography, { sx: { color: "rgba(255,255,255,0.45)", fontWeight: 700 }, children: ":" }), _jsx(Select, { size: "small", value: format(selectedDate !== null && selectedDate !== void 0 ? selectedDate : new Date(), "mm"), onChange: function (event) { return setTimePart("minute", event.target.value); }, MenuProps: { PaperProps: { sx: menuPaperSx } }, sx: {
                                        flex: 1,
                                        color: "#fff",
                                        ".MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT_BORDER },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
                                        ".MuiSelect-icon": { color: "rgba(255,255,255,0.45)" },
                                    }, children: MINUTES.map(function (minute) { return (_jsx(MenuItem, { value: minute, children: minute }, minute)); }) })] })), _jsxs(Box, { display: "flex", justifyContent: "space-between", gap: 1, mt: 1.5, children: [_jsx(Button, { size: "small", startIcon: _jsx(CloseIcon, {}), onClick: function () { return onChange(""); }, sx: {
                                        color: "rgba(255,255,255,0.5)",
                                        textTransform: "none",
                                        "&:hover": { color: "#fff", background: "rgba(255,255,255,0.04)" },
                                    }, children: "Clear" }), _jsx(Button, { size: "small", onClick: setNow, sx: {
                                        color: ACCENT,
                                        border: "1px solid ".concat(ACCENT_BORDER),
                                        borderRadius: "9px",
                                        textTransform: "none",
                                        "&:hover": { background: ACCENT_BG, borderColor: ACCENT },
                                    }, children: mode === "date" ? "Today" : "Now" })] })] }) })] }));
};
